import React from 'react';
import { User, Role } from '../../types/user';
import { User as UserIcon, Trash2, Edit } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  user: User;
  role?: Role;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
};

export const UserCard = ({ user, role, onEdit, onDelete }: Props) => {
  const { direction } = useLanguage();
  const isRTL = direction === 'rtl';

  // Loading skeleton when role is undefined
  if (!role) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-md p-6 relative"
      >
        <div className="animate-pulse">
          <div className="flex items-center gap-4">
            <div className="bg-gray-200 rounded-full w-10 h-10" />
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-32" />
            </div>
          </div>
          <div className="space-y-3 mt-4">
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-4 bg-gray-200 rounded w-full" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-md p-6 relative"
    >
      {/* Action Buttons - Positioned based on direction */}
      <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} flex gap-2`}>
        <button
          onClick={() => onDelete(user)}
          className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
          title={isRTL ? "حذف" : "Delete"}
        >
          <Trash2 size={18} />
        </button>
        <button
          onClick={() => onEdit(user)}
          className="p-2 text-primary-500 hover:bg-primary-50 rounded-full transition-colors"
          title={isRTL ? "تعديل" : "Edit"}
        >
          <Edit size={18} />
        </button>
      </div>

      {/* User Info */}
      <div className={`flex items-center gap-4 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
        <div className={`p-2 rounded-full ${user.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'}`}>
          <UserIcon 
            className={user.gender === 'male' ? 'text-blue-500' : 'text-pink-500'} 
            size={24} 
          />
        </div>
        <div>
          <h3 className="font-semibold text-lg">{user.name}</h3>
          <p className="text-gray-500 text-sm">{user.email}</p>
        </div>
      </div>

      <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
        {/* Role Name */}
        <div className="flex items-center gap-2">
          <div 
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: role.color }}
          />
          <span className="text-sm font-medium truncate">{role.name}</span>
        </div>
      </div>
    </motion.div>
  );
};